import { z } from 'zod';
import { identifierReferenceSchema, locationSnapshotSchema } from './location.js';
import { calendarDateSchema, canonicalJsonStringify, jsonValueSchema, noteSchema, positiveSafeIntegerSchema, recordedMetresSchema, sha256HexUtf8, uuidSchema, zonedMomentSchema, } from './primitives.js';
import { distanceProvenanceSchema, MAX_ROUTE_VIA_POINTS, routeConfirmationSchema, routeFingerprintSha256, routeSnapshotSchema, routeSeedSchema, } from './route.js';
import { routeCopySourceProofSchema, routeDraftCatalogDocumentIdSchema, routeDraftCountryCodeSchema, routeDraftDistanceInputModeSchema, routeDraftEndpointSchema, routeDraftOfferSourceSchema, routeDraftSelectionSchema, } from './route-draft.js';
import { crewRoleSchema, tripSegmentStatusSchema, workTypeSchema } from './shift.js';
import { artifactVersionSchema } from './versioning.js';
import { vehicleSnapshotSchema } from './vehicle.js';
/**
 * Draft text is deliberately raw: autosave must preserve ordinary intermediate
 * input, including leading or trailing whitespace. Canonical non-empty text is
 * required only when the editor projects a draft into final domain entities.
 */
export const shiftEditorDraftLocationNameSchema = z.string().max(512);
export const shiftEditorDraftEarlyTerminationReasonSchema = z.string().max(1_000);
export const shiftEditorDraftTrainOrServiceNumberSchema = z.string().max(128);
export const shiftEditorDraftOperatorSchema = z.string().max(256);
export const shiftEditorDraftCountryCodeSchema = routeDraftCountryCodeSchema;
export const shiftEditorDraftDistanceInputModeSchema = routeDraftDistanceInputModeSchema;
export const shiftEditorDraftCatalogDocumentIdSchema = routeDraftCatalogDocumentIdSchema;
const shiftEditorDraftConfirmedEndpointV1Schema = z.strictObject({
    location_id: uuidSchema,
    name: z
        .string()
        .min(1)
        .max(512)
        .refine((value) => value.trim() === value),
    country_code: shiftEditorDraftCountryCodeSchema,
    key_identifier: identifierReferenceSchema.nullable().default(null),
});
export const shiftEditorDraftConfirmedEndpointSchema = z
    .strictObject({
    location_id: uuidSchema,
    name: z
        .string()
        .min(1)
        .max(512)
        .refine((value) => value.trim() === value),
    country_code: shiftEditorDraftCountryCodeSchema,
    key_identifier: identifierReferenceSchema.nullable(),
    catalog_document_id: shiftEditorDraftCatalogDocumentIdSchema.nullable(),
    catalog_version: artifactVersionSchema.nullable(),
})
    .superRefine(validateCatalogProvenancePair);
const shiftEditorDraftRouteConfirmationV1Schema = z.strictObject({
    evidence: routeConfirmationSchema,
    origin: shiftEditorDraftConfirmedEndpointV1Schema,
    destination: shiftEditorDraftConfirmedEndpointV1Schema,
});
const shiftEditorDraftRouteConfirmationV2Schema = z.strictObject({
    evidence: routeConfirmationSchema,
    origin: shiftEditorDraftConfirmedEndpointSchema,
    destination: shiftEditorDraftConfirmedEndpointSchema,
});
export const shiftEditorDraftDistanceConfirmationSchema = z.strictObject({
    input_mode: shiftEditorDraftDistanceInputModeSchema,
    value: distanceProvenanceSchema,
});
const shiftEditorDraftRouteConfirmationV3Schema = z.strictObject({
    evidence: routeConfirmationSchema,
    origin: shiftEditorDraftConfirmedEndpointSchema,
    destination: shiftEditorDraftConfirmedEndpointSchema,
    distance: shiftEditorDraftDistanceConfirmationSchema,
});
export const shiftEditorDraftRouteOfferSourceSchema = routeDraftOfferSourceSchema;
export const shiftEditorDraftRouteSelectionSchema = routeDraftSelectionSchema;
export const shiftEditorDraftRouteConfirmationSchema = z.strictObject({
    evidence: routeConfirmationSchema,
    selection: shiftEditorDraftRouteSelectionSchema,
    origin: shiftEditorDraftConfirmedEndpointSchema,
    via: z.array(shiftEditorDraftConfirmedEndpointSchema).max(MAX_ROUTE_VIA_POINTS),
    destination: shiftEditorDraftConfirmedEndpointSchema,
    distance: shiftEditorDraftDistanceConfirmationSchema,
});
const shiftEditorDraftEndpointV1Schema = z
    .strictObject({
    location_id: uuidSchema,
    name: shiftEditorDraftLocationNameSchema,
    country_code: shiftEditorDraftCountryCodeSchema,
    key_identifier: identifierReferenceSchema.nullable().default(null),
    carried_location_snapshot: locationSnapshotSchema.nullable(),
})
    .superRefine(validateCarriedLocationSnapshot);
export const shiftEditorDraftEndpointSchema = routeDraftEndpointSchema;
/**
 * Vehicle fields stay raw while a row is being edited. The UUID is allocated
 * once and remains stable across reorder/removal; final vehicle snapshots are
 * canonicalized only at the commit boundary.
 */
export const shiftEditorDraftVehicleSnapshotSchema = z.strictObject({
    vehicle_id: uuidSchema,
    series: z.string().max(512),
    full_number: z.string().max(64),
    nickname: z.string().max(128),
    operator: z.string().max(256),
    vehicle_type: vehicleSnapshotSchema.shape.vehicle_type.nullable(),
});
/** Exact production wire contract retained solely as a migration input. */
export const shiftEditorDraftV1Schema = z
    .strictObject({
    contract_version: z.literal('shift_editor_draft.v1'),
    base_aggregate_revision: positiveSafeIntegerSchema,
    shift_id: uuidSchema,
    trip_segment_id: uuidSchema,
    route_variant_id: uuidSchema,
    service_date: calendarDateSchema,
    origin: shiftEditorDraftEndpointV1Schema,
    destination: shiftEditorDraftEndpointV1Schema.nullable(),
    route_confirmation: shiftEditorDraftRouteConfirmationV1Schema.nullable(),
})
    .superRefine(validateRouteConfirmationBinding);
export const shiftEditorDraftV2Schema = z
    .strictObject({
    contract_version: z.literal('shift_editor_draft.v2'),
    base_aggregate_revision: positiveSafeIntegerSchema,
    shift_id: uuidSchema,
    trip_segment_id: uuidSchema,
    route_variant_id: uuidSchema,
    service_date: calendarDateSchema,
    actual_start: zonedMomentSchema.nullable(),
    actual_end: zonedMomentSchema.nullable(),
    origin: shiftEditorDraftEndpointSchema,
    destination: shiftEditorDraftEndpointSchema.nullable(),
    planned_destination: shiftEditorDraftEndpointSchema.nullable(),
    early_termination_reason: shiftEditorDraftEarlyTerminationReasonSchema,
    /** Missing on legacy v2 payloads; null means no inherited route data. */
    route_seed: routeSeedSchema.nullable().exactOptional(),
    route_confirmation: shiftEditorDraftRouteConfirmationV2Schema.nullable(),
    train_or_service_number: shiftEditorDraftTrainOrServiceNumberSchema,
    work_type: workTypeSchema.nullable(),
    role: crewRoleSchema.nullable(),
    operator: shiftEditorDraftOperatorSchema,
    vehicles: z.array(shiftEditorDraftVehicleSnapshotSchema).max(16),
    distance_metres: recordedMetresSchema.nullable(),
    note: noteSchema,
    tags: z.array(z.string().min(1).max(64)).max(64),
    status: tripSegmentStatusSchema,
})
    .superRefine((value, context) => {
    validateRouteConfirmationBinding(value, context);
    validateUniqueDraftCollections(value, context);
});
/** Exact production v3 wire contract retained solely as a migration input. */
export const shiftEditorDraftV3Schema = z
    .strictObject({
    contract_version: z.literal('shift_editor_draft.v3'),
    base_aggregate_revision: positiveSafeIntegerSchema,
    shift_id: uuidSchema,
    trip_segment_id: uuidSchema,
    route_variant_id: uuidSchema,
    service_date: calendarDateSchema,
    actual_start: zonedMomentSchema.nullable(),
    actual_end: zonedMomentSchema.nullable(),
    origin: shiftEditorDraftEndpointSchema,
    destination: shiftEditorDraftEndpointSchema.nullable(),
    planned_destination: shiftEditorDraftEndpointSchema.nullable(),
    early_termination_reason: shiftEditorDraftEarlyTerminationReasonSchema,
    /** Missing on legacy payloads; null means no inherited route data. */
    route_seed: routeSeedSchema.nullable().exactOptional(),
    route_confirmation: shiftEditorDraftRouteConfirmationV3Schema.nullable(),
    train_or_service_number: shiftEditorDraftTrainOrServiceNumberSchema,
    work_type: workTypeSchema.nullable(),
    role: crewRoleSchema.nullable(),
    operator: shiftEditorDraftOperatorSchema,
    vehicles: z.array(shiftEditorDraftVehicleSnapshotSchema).max(16),
    distance_input_mode: shiftEditorDraftDistanceInputModeSchema,
    distance_metres: recordedMetresSchema.nullable(),
    note: noteSchema,
    tags: z.array(z.string().min(1).max(64)).max(64),
    status: tripSegmentStatusSchema,
})
    .superRefine((value, context) => {
    validateRouteConfirmationBinding(value, context);
    validateDistanceBinding(value, context);
    validateUniqueDraftCollections(value, context);
});
const shiftEditorDraftSharedShape = {
    base_aggregate_revision: positiveSafeIntegerSchema,
    shift_id: uuidSchema,
    trip_segment_id: uuidSchema,
    route_variant_id: uuidSchema,
    service_date: calendarDateSchema,
    actual_start: zonedMomentSchema.nullable(),
    actual_end: zonedMomentSchema.nullable(),
    origin: shiftEditorDraftEndpointSchema,
    via: z.array(shiftEditorDraftEndpointSchema).max(MAX_ROUTE_VIA_POINTS),
    destination: shiftEditorDraftEndpointSchema.nullable(),
    planned_destination: shiftEditorDraftEndpointSchema.nullable(),
    early_termination_reason: shiftEditorDraftEarlyTerminationReasonSchema,
    /** Null means that the user has not explicitly selected a route yet. */
    route_selection: shiftEditorDraftRouteSelectionSchema.nullable(),
    /** Null means no inherited route data. */
    route_seed: routeSeedSchema.nullable(),
    route_confirmation: shiftEditorDraftRouteConfirmationSchema.nullable(),
    train_or_service_number: shiftEditorDraftTrainOrServiceNumberSchema,
    work_type: workTypeSchema.nullable(),
    role: crewRoleSchema.nullable(),
    operator: shiftEditorDraftOperatorSchema,
    vehicles: z.array(shiftEditorDraftVehicleSnapshotSchema).max(16),
    distance_input_mode: shiftEditorDraftDistanceInputModeSchema,
    distance_metres: recordedMetresSchema.nullable(),
    note: noteSchema,
    tags: z.array(z.string().min(1).max(64)).max(64),
    status: tripSegmentStatusSchema,
};
/** Exact production v4 wire contract retained solely as a migration input. */
export const shiftEditorDraftV4Schema = z
    .strictObject({
    contract_version: z.literal('shift_editor_draft.v4'),
    ...shiftEditorDraftSharedShape,
})
    .superRefine((value, context) => {
    validateRouteConfirmationBinding(value, context);
    validateRouteSeedViaBinding(value, context);
    validateDistanceBinding(value, context);
    validateUniqueDraftCollections(value, context);
});
/** Exact unreleased v5 proof retained solely as a fail-closed migration input. */
export const shiftEditorDraftRouteCopySourceV5Schema = z.strictObject({
    shift_id: uuidSchema,
    aggregate_revision: positiveSafeIntegerSchema,
    trip_segment_id: uuidSchema,
    route_variant_id: uuidSchema,
});
/** Exact unreleased v5 wire contract retained solely as a migration input. */
export const shiftEditorDraftV5Schema = z
    .strictObject({
    contract_version: z.literal('shift_editor_draft.v5'),
    ...shiftEditorDraftSharedShape,
    /**
     * Exact live source of a copy shortcut. Consumers must re-prove this
     * reference against the named aggregate revision before projecting a
     * source snapshot through a canonical live route identity.
     */
    route_copy_source: shiftEditorDraftRouteCopySourceV5Schema.nullable(),
})
    .superRefine((value, context) => {
    validateRouteConfirmationBinding(value, context);
    validateRouteSeedViaBinding(value, context);
    validateDistanceBinding(value, context);
    validateUniqueDraftCollections(value, context);
    validateRouteCopySourceV5Binding(value, context);
});
export const shiftEditorDraftRouteCopySourceSchema = routeCopySourceProofSchema;
export const shiftEditorDraftSchema = z
    .strictObject({
    contract_version: z.literal('shift_editor_draft.v6'),
    ...shiftEditorDraftSharedShape,
    /**
     * Durable proof for a copied exact route snapshot. It grants no authority:
     * composer and commit boundaries must revalidate it against the current
     * live source segment and its immutable route snapshot.
     */
    route_copy_source: shiftEditorDraftRouteCopySourceSchema.nullable(),
})
    .superRefine((value, context) => {
    validateRouteConfirmationBinding(value, context);
    validateRouteSeedViaBinding(value, context);
    validateDistanceBinding(value, context);
    validateUniqueDraftCollections(value, context);
    validateRouteCopySourceBinding(value, context);
});
const shiftEditorDraftWireSchema = z.union([
    shiftEditorDraftSchema,
    shiftEditorDraftV5Schema,
    shiftEditorDraftV4Schema,
    shiftEditorDraftV3Schema,
    shiftEditorDraftV2Schema,
    shiftEditorDraftV1Schema,
]);
/**
 * Boundary parser for untrusted IndexedDB or JSON payloads. The JSON-value
 * gate rejects Dates, undefined, non-finite numbers, cycles and hidden or
 * symbol properties before the strict versioned domain schema is evaluated.
 * Production v1-v4 values are upgraded without inventing missing user intent;
 * legacy confirmation is discarded because no durable selection action exists.
 */
export function parseShiftEditorDraft(input) {
    const parsed = shiftEditorDraftWireSchema.parse(jsonValueSchema.parse(input));
    switch (parsed.contract_version) {
        case 'shift_editor_draft.v6':
            return parsed;
        case 'shift_editor_draft.v5':
            return migrateShiftEditorDraftV5(parsed);
        case 'shift_editor_draft.v4':
            return migrateShiftEditorDraftV4(parsed);
        case 'shift_editor_draft.v3':
            return migrateShiftEditorDraftV3(parsed);
        case 'shift_editor_draft.v2':
            return migrateShiftEditorDraftV2(parsed);
        case 'shift_editor_draft.v1':
            return migrateShiftEditorDraftV1(parsed);
    }
}
/**
 * Exact v3 projection stored by the compatibility-reader release. A rollback
 * reader can always open it; the same row carries the lossless v6 payload in
 * an additive top-level field that old readers ignore.
 */
export function projectShiftEditorDraftForV3Rollback(input) {
    const draft = shiftEditorDraftSchema.parse(input);
    return shiftEditorDraftV3Schema.parse({
        contract_version: 'shift_editor_draft.v3',
        base_aggregate_revision: draft.base_aggregate_revision,
        shift_id: draft.shift_id,
        trip_segment_id: draft.trip_segment_id,
        route_variant_id: draft.route_variant_id,
        service_date: draft.service_date,
        actual_start: draft.actual_start,
        actual_end: draft.actual_end,
        origin: draft.origin,
        destination: draft.destination,
        planned_destination: draft.planned_destination,
        early_termination_reason: draft.early_termination_reason,
        route_seed: draft.route_seed,
        // v3 cannot truthfully encode explicit selection or ordered confirmed via.
        // The rollback reader therefore requires a fresh confirmation.
        route_confirmation: null,
        train_or_service_number: draft.train_or_service_number,
        work_type: draft.work_type,
        role: draft.role,
        operator: draft.operator,
        vehicles: draft.vehicles,
        distance_input_mode: draft.distance_input_mode,
        distance_metres: draft.distance_metres,
        note: draft.note,
        tags: draft.tags,
        status: draft.status,
    });
}
function migrateShiftEditorDraftV5(input) {
    return shiftEditorDraftSchema.parse({
        ...input,
        contract_version: 'shift_editor_draft.v6',
        // The aggregate-bound v5 proof becomes stale on the first target commit
        // and cannot be safely rebased without re-proving its source snapshot.
        route_copy_source: null,
    });
}
export function routeCopySourceSnapshotSha256(input) {
    const snapshot = routeSnapshotSchema.parse(input);
    return sha256HexUtf8(`dmf.route-copy-source-snapshot.sha256.v1\n${canonicalJsonStringify(snapshot)}`);
}
function migrateShiftEditorDraftV4(input) {
    return shiftEditorDraftSchema.parse({
        ...input,
        contract_version: 'shift_editor_draft.v6',
        route_copy_source: null,
    });
}
function migrateShiftEditorDraftV3(input) {
    return shiftEditorDraftSchema.parse({
        ...input,
        contract_version: 'shift_editor_draft.v6',
        via: (input.route_seed ?? null)?.via.map(endpointFromLocationSnapshot) ?? [],
        route_selection: null,
        route_seed: input.route_seed ?? null,
        // Legacy drafts did not persist an explicit selection action or confirmed via inputs.
        route_confirmation: null,
        route_copy_source: null,
    });
}
function migrateShiftEditorDraftV1(input) {
    return shiftEditorDraftSchema.parse({
        contract_version: 'shift_editor_draft.v6',
        base_aggregate_revision: input.base_aggregate_revision,
        shift_id: input.shift_id,
        trip_segment_id: input.trip_segment_id,
        route_variant_id: input.route_variant_id,
        service_date: input.service_date,
        actual_start: null,
        actual_end: null,
        origin: migrateEndpointV1(input.origin),
        via: [],
        destination: input.destination === null ? null : migrateEndpointV1(input.destination),
        planned_destination: null,
        early_termination_reason: '',
        route_selection: null,
        route_seed: null,
        route_confirmation: null,
        route_copy_source: null,
        train_or_service_number: '',
        work_type: null,
        role: null,
        operator: '',
        vehicles: [],
        distance_input_mode: 'none',
        distance_metres: null,
        note: '',
        tags: [],
        status: 'complete',
    });
}
function migrateShiftEditorDraftV2(input) {
    const inherited = input.route_seed !== undefined &&
        input.route_seed !== null &&
        input.distance_metres === input.route_seed.distance.metres;
    const distanceInputMode = inherited
        ? 'inherited'
        : input.distance_metres === null
            ? 'none'
            : 'manual_override';
    return shiftEditorDraftSchema.parse({
        ...input,
        contract_version: 'shift_editor_draft.v6',
        via: (input.route_seed ?? null)?.via.map(endpointFromLocationSnapshot) ?? [],
        route_selection: null,
        route_seed: input.route_seed ?? null,
        distance_input_mode: distanceInputMode,
        // Legacy drafts did not persist an explicit selection action.
        route_confirmation: null,
        route_copy_source: null,
    });
}
function migrateEndpointV1(endpoint) {
    return {
        ...endpoint,
        catalog_document_id: null,
        catalog_version: null,
    };
}
function endpointFromLocationSnapshot(snapshot) {
    return {
        location_id: snapshot.location_id,
        name: snapshot.display_name,
        country_code: shiftEditorDraftCountryCodeSchema.parse(snapshot.country_code),
        key_identifier: snapshot.key_identifiers[0] ?? null,
        carried_location_snapshot: snapshot,
        catalog_document_id: null,
        catalog_version: null,
    };
}
/**
 * Projects the editor's distance controls into the immutable provenance that
 * must be captured at route-confirmation time. Invalid cross-field states are
 * rejected instead of being guessed or silently canonicalized.
 */
export function shiftEditorDraftDistanceForConfirmation(input, confirmedAt) {
    switch (input.distance_input_mode) {
        case 'none':
            if (input.distance_metres !== null) {
                throw new TypeError('none distance input mode requires null distance_metres');
            }
            return unknownDistanceProvenance();
        case 'inherited':
            if (input.route_seed === undefined || input.route_seed === null) {
                throw new TypeError('inherited distance input mode requires route_seed');
            }
            if (input.distance_metres !== input.route_seed.distance.metres) {
                throw new TypeError('inherited distance must exactly match route_seed.distance.metres');
            }
            return distanceProvenanceSchema.parse(input.route_seed.distance);
        case 'manual_override':
            if (input.distance_metres === null) {
                throw new TypeError('manual_override distance input mode requires distance_metres');
            }
            return distanceProvenanceSchema.parse({
                metres: input.distance_metres,
                provenance: 'manual',
                quality: 'confirmed',
                calculation_basis: 'explicit_user_entry',
                source_artifact: null,
                source_record_id: null,
                user_confirmed_at: confirmedAt,
            });
    }
}
function unknownDistanceProvenance() {
    return {
        metres: null,
        provenance: 'unknown',
        quality: 'unknown',
        calculation_basis: 'none',
        source_artifact: null,
        source_record_id: null,
        user_confirmed_at: null,
    };
}
function validateRouteConfirmationBinding(value, context) {
    const destination = value.destination;
    const via = value.via ?? [];
    const endpointsComplete = value.origin.name !== '' &&
        via.every((endpoint) => endpoint.name !== '') &&
        destination?.name !== '';
    if (value.route_confirmation === null) {
        return;
    }
    if (!endpointsComplete || destination === null) {
        context.addIssue({
            code: 'custom',
            path: ['route_confirmation'],
            message: 'an incomplete actual route cannot retain confirmation',
        });
        return;
    }
    if ('route_selection' in value) {
        if (value.route_selection === null) {
            context.addIssue({
                code: 'custom',
                path: ['route_selection'],
                message: 'route confirmation requires a durable explicit route selection',
            });
        }
        else if (value.route_confirmation.selection === undefined ||
            canonicalJsonStringify(value.route_selection) !==
                canonicalJsonStringify(value.route_confirmation.selection)) {
            context.addIssue({
                code: 'custom',
                path: ['route_confirmation', 'selection'],
                message: 'route confirmation must retain the exact durable route selection',
            });
        }
    }
    if (!sameEndpointProjection(value.origin, value.route_confirmation.origin)) {
        context.addIssue({
            code: 'custom',
            path: ['route_confirmation', 'origin'],
            message: 'draft route confirmation must retain the exact confirmed origin input',
        });
    }
    if (!sameEndpointProjection(destination, value.route_confirmation.destination)) {
        context.addIssue({
            code: 'custom',
            path: ['route_confirmation', 'destination'],
            message: 'draft route confirmation must retain the exact confirmed destination input',
        });
    }
    if ('via' in value) {
        const confirmedVia = value.route_confirmation.via ?? [];
        if (via.length !== confirmedVia.length ||
            via.some((endpoint, index) => {
                const confirmed = confirmedVia[index];
                return confirmed === undefined || !sameEndpointProjection(endpoint, confirmed);
            })) {
            context.addIssue({
                code: 'custom',
                path: ['route_confirmation', 'via'],
                message: 'draft route confirmation must retain the exact ordered confirmed via inputs',
            });
        }
    }
    const expectedFingerprint = routeFingerprintSha256({
        candidate_source: value.route_seed?.candidate_source ?? 'manual',
        destination_location_id: destination.location_id,
        graph_sha256: value.route_seed?.graph_version?.sha256 ?? null,
        network_edge_ids: value.route_seed?.network_segments.map((segment) => segment.graph_edge_id) ?? [],
        origin_location_id: value.origin.location_id,
        via_location_ids: 'via' in value
            ? via.map((location) => location.location_id)
            : (value.route_seed?.via.map((location) => location.location_id) ?? []),
    });
    if (value.route_confirmation.evidence.route_fingerprint_sha256 !== expectedFingerprint) {
        context.addIssue({
            code: 'custom',
            path: ['route_confirmation', 'evidence', 'route_fingerprint_sha256'],
            message: 'draft route confirmation must bind the current manual origin and destination identity',
        });
    }
}
function validateRouteSeedViaBinding(value, context) {
    if (value.route_selection?.kind === 'manual' && value.route_seed !== null) {
        context.addIssue({
            code: 'custom',
            path: ['route_seed'],
            message: 'a manually selected route cannot retain inherited route seed provenance',
        });
    }
    if (value.route_selection?.kind === 'offer' && value.route_seed === null) {
        context.addIssue({
            code: 'custom',
            path: ['route_seed'],
            message: 'an offered route selection requires its immutable route seed',
        });
    }
    const routeSeed = value.route_seed;
    if (routeSeed === null)
        return;
    if (value.via.length !== routeSeed.via.length ||
        value.via.some((endpoint, index) => {
            const snapshot = routeSeed.via[index];
            return (snapshot === undefined ||
                endpoint.carried_location_snapshot === null ||
                canonicalJsonStringify(endpoint.carried_location_snapshot) !==
                    canonicalJsonStringify(snapshot));
        })) {
        context.addIssue({
            code: 'custom',
            path: ['via'],
            message: 'draft via inputs must exactly retain the immutable ordered route-seed snapshots',
        });
    }
}
function validateRouteCopySourceBinding(value, context) {
    const source = value.route_copy_source;
    if (source === null)
        return;
    if (source.source_shift_id !== value.shift_id) {
        context.addIssue({
            code: 'custom',
            path: ['route_copy_source', 'source_shift_id'],
            message: 'a route-copy source must belong to the draft shift',
        });
    }
    if (source.source_trip_segment_id === value.trip_segment_id) {
        context.addIssue({
            code: 'custom',
            path: ['route_copy_source', 'source_trip_segment_id'],
            message: 'a copied route must target a new segment identity',
        });
    }
    if (value.route_selection?.kind !== 'offer' ||
        value.route_selection.offer_source !== 'history' ||
        value.route_selection.selection_method !== 'explicit' ||
        value.route_seed === null) {
        context.addIssue({
            code: 'custom',
            path: ['route_copy_source'],
            message: 'a route-copy source requires an explicit seeded history selection',
        });
    }
}
function validateRouteCopySourceV5Binding(value, context) {
    const source = value.route_copy_source;
    if (source === null)
        return;
    if (source.shift_id !== value.shift_id) {
        context.addIssue({
            code: 'custom',
            path: ['route_copy_source', 'shift_id'],
            message: 'a route-copy source must belong to the draft shift',
        });
    }
    if (source.aggregate_revision !== value.base_aggregate_revision) {
        context.addIssue({
            code: 'custom',
            path: ['route_copy_source', 'aggregate_revision'],
            message: 'a route-copy source must bind the exact draft aggregate revision',
        });
    }
    if (source.trip_segment_id === value.trip_segment_id) {
        context.addIssue({
            code: 'custom',
            path: ['route_copy_source', 'trip_segment_id'],
            message: 'a copied route must target a new segment identity',
        });
    }
    if (value.route_selection?.kind !== 'offer' ||
        value.route_selection.offer_source !== 'history' ||
        value.route_selection.selection_method !== 'explicit' ||
        value.route_seed === null) {
        context.addIssue({
            code: 'custom',
            path: ['route_copy_source'],
            message: 'a route-copy source requires an explicit seeded history selection',
        });
    }
}
function validateDistanceBinding(value, context) {
    let validModeState = true;
    if (value.distance_input_mode === 'none' && value.distance_metres !== null) {
        validModeState = false;
        context.addIssue({
            code: 'custom',
            path: ['distance_metres'],
            message: 'none distance input mode requires null distance_metres',
        });
    }
    if (value.distance_input_mode === 'inherited') {
        if (value.route_seed === undefined || value.route_seed === null) {
            validModeState = false;
            context.addIssue({
                code: 'custom',
                path: ['route_seed'],
                message: 'inherited distance input mode requires route_seed',
            });
        }
        else if (value.distance_metres !== value.route_seed.distance.metres) {
            validModeState = false;
            context.addIssue({
                code: 'custom',
                path: ['distance_metres'],
                message: 'inherited distance must exactly match route_seed.distance.metres',
            });
        }
    }
    if (value.distance_input_mode === 'manual_override' && value.distance_metres === null) {
        validModeState = false;
        context.addIssue({
            code: 'custom',
            path: ['distance_metres'],
            message: 'manual_override distance input mode requires distance_metres',
        });
    }
    const confirmation = value.route_confirmation;
    if (confirmation === null || !validModeState)
        return;
    if (confirmation.distance.input_mode !== value.distance_input_mode) {
        context.addIssue({
            code: 'custom',
            path: ['route_confirmation', 'distance', 'input_mode'],
            message: 'route confirmation must retain the exact confirmed distance input mode',
        });
    }
    const expectedDistance = shiftEditorDraftDistanceForConfirmation(value, confirmation.evidence.confirmed_at);
    if (!sameDistanceProvenance(confirmation.distance.value, expectedDistance)) {
        context.addIssue({
            code: 'custom',
            path: ['route_confirmation', 'distance', 'value'],
            message: 'route confirmation must retain the exact confirmed distance provenance',
        });
    }
}
function validateUniqueDraftCollections(value, context) {
    const vehicleIds = value.vehicles.map((vehicle) => vehicle.vehicle_id);
    if (new Set(vehicleIds).size !== vehicleIds.length) {
        context.addIssue({
            code: 'custom',
            path: ['vehicles'],
            message: 'draft vehicle IDs must be unique within a segment',
        });
    }
    if (new Set(value.tags).size !== value.tags.length) {
        context.addIssue({
            code: 'custom',
            path: ['tags'],
            message: 'tags must be unique and stored in semantic order',
        });
    }
}
function validateCarriedLocationSnapshot(value, context) {
    const snapshot = value.carried_location_snapshot;
    if (snapshot === null) {
        return;
    }
    if (snapshot.location_id !== value.location_id) {
        context.addIssue({
            code: 'custom',
            path: ['carried_location_snapshot', 'location_id'],
            message: 'a carried location snapshot must retain its historical location identity',
        });
    }
    if (snapshot.display_name !== value.name) {
        context.addIssue({
            code: 'custom',
            path: ['carried_location_snapshot', 'display_name'],
            message: 'a carried location snapshot name must not be edited in place',
        });
    }
    if (snapshot.country_code !== value.country_code) {
        context.addIssue({
            code: 'custom',
            path: ['carried_location_snapshot', 'country_code'],
            message: 'a carried location snapshot country must not be edited in place',
        });
    }
    if (!sameNullableIdentifier(snapshot.key_identifiers[0] ?? null, value.key_identifier)) {
        context.addIssue({
            code: 'custom',
            path: ['key_identifier'],
            message: 'a carried location snapshot identifier must not be edited in place',
        });
    }
}
function validateCatalogProvenancePair(value, context) {
    if ((value.catalog_document_id === null) !== (value.catalog_version === null)) {
        context.addIssue({
            code: 'custom',
            path: [value.catalog_document_id === null ? 'catalog_document_id' : 'catalog_version'],
            message: 'catalog_document_id and catalog_version must be both present or both null',
        });
    }
}
function sameEndpointProjection(current, confirmed) {
    return (current.location_id === confirmed.location_id &&
        current.name === confirmed.name &&
        current.country_code === confirmed.country_code &&
        sameNullableIdentifier(current.key_identifier, confirmed.key_identifier) &&
        (current.catalog_document_id ?? null) === (confirmed.catalog_document_id ?? null) &&
        sameNullableArtifact(current.catalog_version ?? null, confirmed.catalog_version ?? null));
}
function sameNullableIdentifier(left, right) {
    if (left === null || right === null)
        return left === right;
    return (left.namespace === right.namespace &&
        left.namespace_other === right.namespace_other &&
        left.value === right.value);
}
function sameNullableArtifact(left, right) {
    if (left === null || right === null)
        return left === right;
    return left.version === right.version && left.sha256 === right.sha256;
}
function sameDistanceProvenance(left, right) {
    return (canonicalJsonStringify(left) ===
        canonicalJsonStringify(right));
}
//# sourceMappingURL=shift-editor-draft.js.map